import { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorActions } from '@atlaskit/editor-core';
import type { EditorContentMode } from '@atlaskit/editor-common/types';

import { getForgeExtensionProvider } from '@atlassian/xen-editor-provider';
import type { GetContextTokenOptions } from '@atlassian/forge-ui';

import type { UseGetContextTokenParams } from '@confluence/forge-ui/entry-points/useContextToken.ts';
import { getWorkspaceARI } from '@confluence/forge-ui/entry-points/useWorkspaceARI.ts';
import { GQLExtensionContextsFilterType } from '@confluence/forge-ui/entry-points/provider.ts';
import { FlagProvider } from '@confluence/forge-ui/entry-points/flagProvider.ts';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData.ts';
import {
	environmentToForgeProductEnvironment,
	createForgeUIAnalyticsContext,
	createForgeMacroExtensionData,
} from '@confluence/fabric-providers/entry-points/ForgeProvider.ts';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { fetchSpaceDetail } from '@confluence/space-utils/entry-points/fetchSpaceDetail.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useRouteActions } from '@confluence/route-manager/entry-points/RouteState.ts';

import { type EditorAPI } from './extensionHandlerTypes.ts';

type ForgeExtensionProviderParams = Parameters<typeof getForgeExtensionProvider>[0];

export interface GetForgeExtensionHandlerParams {
	spaceKey: string;
	spaceId?: string;
	contentId: string;
	contentType: any;
	contentMode?: EditorContentMode;
	mentionProvider: any;
	isEditing: boolean;
	isLivePage: boolean;
	connectExtensionProvider?: ForgeExtensionProviderParams['connectExtensionProvider'];
	editorActions?: EditorActions;
	hideGlassPaneForForge?: boolean;
	locale?: string;
	editorAPI?: EditorAPI;
	templateId?: string;
	useGetContextToken?: (
		params: UseGetContextTokenParams,
	) => (options?: GetContextTokenOptions) => Promise<string>;
}

export const getForgeExtensionHandler = async ({
	spaceKey,
	spaceId,
	contentId,
	contentType,
	mentionProvider,
	isEditing,
	contentMode,
	connectExtensionProvider,
	editorActions,
	hideGlassPaneForForge,
	locale,
	isLivePage = false,
	editorAPI,
	useGetContextToken,
	templateId,
}: GetForgeExtensionHandlerParams) => {
	const { cloudId, userId, environment, activationId } = await getSessionData();
	const accountId = userId ?? '';

	const apolloClient = getAGGClient();
	const contextIds = [getWorkspaceARI({ cloudId, activationId })];
	const providerFactory = ProviderFactory.create(mentionProvider);

	const getProviderOptions = ({
		spaceKey: spaceKeyArg,
		spaceId: spaceIdArg,
	}: {
		spaceKey: string;
		spaceId?: string;
	}) => ({
		accountId,
		cloudId,
		apolloClient,
		contextIds,
		dataProviders: providerFactory,
		environment: environmentToForgeProductEnvironment(environment),
		product: 'confluence',
		page: 'confluence:macroEditor',
		isEditing,
		contentMode,
		analyticsWebClient: getAnalyticsWebClient(),
		forgeUIAnalyticsContext: createForgeUIAnalyticsContext({
			objectId: contentId,
			objectType: contentType,
			containerId: spaceKeyArg, // TODO replace with spaceId
			containerType: 'space',
		}),
		extensionData: createForgeMacroExtensionData({
			spaceKey: spaceKeyArg,
			spaceId: spaceIdArg ?? spaceId,
			contentId,
			contentType,
			isLivePage,
			templateId,
		}),
		connectExtensionProvider,
		editorActions,
		FlagProvider,
		extensionsFilter: undefined,
		hideGlassPane: hideGlassPaneForForge,
		locale,
		editorAPI,
		useGetContextToken,
		useRouteActions,
	});

	const forgeExtensionProvider = fetchForgeExtensionProvider({
		getProviderOptions,
		spaceKey,
	});

	return forgeExtensionProvider;
};

export const fetchForgeExtensionProvider = async ({ getProviderOptions, spaceKey }) => {
	return fetchSpaceDetail(spaceKey)
		.then(({ data, loading, error }) => {
			if (!loading) {
				const dataClassificationTags = data?.space?.dataClassificationTags;
				const spaceKeyFromQuery = data?.space?.key;
				const spaceIdFromQuery = data?.space?.id;

				return { dataClassificationTags, spaceKeyFromQuery, spaceIdFromQuery, error };
			}
		})
		.then((data) => {
			if (data?.error) {
				return Promise.reject(data.error);
			}

			const providerOptions = getProviderOptions({
				spaceKey: data?.spaceKeyFromQuery,
				spaceId: data?.spaceIdFromQuery,
			});

			// TODO: Should always use new extensions query regardless of whether there's dataClassificationTags
			// Remove as part of https://product-fabric.atlassian.net/browse/CCECO-3476
			const shouldUseNewExtensionsQuery = !!(
				data?.dataClassificationTags && data?.dataClassificationTags.length > 0
			);

			if (shouldUseNewExtensionsQuery) {
				providerOptions.extensionsFilter = [
					{
						type: GQLExtensionContextsFilterType.ExtensionType,
						value: ['xen:macro'],
					},
				];

				providerOptions.dataClassificationTagIds = data?.dataClassificationTags;
			}

			return getForgeExtensionProvider(providerOptions);
		});
};
